/**
 * Per-doctype Kanban customization registry.
 *
 * The owning app assigns it in `{doctype}_kanban.js`:
 *   frappe.kanban_v2.settings["Task"] = {
 *     card_context_menu() { ... },
 *     bulk_actions() { ... },
 *   };
 *
 * Other apps (hook `doctype_kanban_js`) extend it so the base stays:
 *   frappe.kanban_v2.extend_settings("Task", (super_) => ({
 *     card_context_menu(card, page) {
 *       return [
 *         ...(super_.card_context_menu?.(card, page) || []),
 *         { label: __("My Action"), onclick: () => {} },
 *       ];
 *     },
 *     bulk_actions(ids, page) {
 *       return [
 *         ...(super_.bulk_actions?.(ids, page) || []),
 *         { label: __("My Bulk"), onclick(ids, page, done) {} },
 *       ];
 *     },
 *     select_styles: { ...super_.select_styles, blocked: "red" },
 *   }));
 *
 * An object patch merges instead: menu and bulk items append, other maps merge.
 *   frappe.kanban_v2.extend_settings("Task", {
 *     card_context_menu(card, page) {
 *       return [{ label: __("My Action"), onclick: () => {} }];
 *     },
 *     bulk_actions(ids, page) {
 *       return [{ label: __("My Bulk"), onclick(ids, page, done) {} }];
 *     },
 *   });
 *
 * Move callbacks (canMoveCard, onBeforeCardMove, onCardMove, onAfterCardMove)
 * run once per card, also on multi-card drags; `move.cardIds` lists every card.
 * onBeforeCardMove may be async; returning false cancels the whole drag.
 */
frappe.provide("frappe.kanban_v2.settings");

frappe.kanban_v2.extend_settings = function (doctype, patch) {
	const parent = copy_kanban_settings(frappe.kanban_v2.settings[doctype] || {});
	const next =
		typeof patch === "function"
			? patch(parent) || {}
			: merge_kanban_settings(parent, patch || {});
	frappe.kanban_v2.settings[doctype] = next;
	return next;
};

function copy_kanban_settings(src) {
	return {
		...src,
		callbacks: { ...(src.callbacks || {}) },
		select_styles: { ...(src.select_styles || {}) },
		options: { ...(src.options || {}) },
		boards: { ...(src.boards || {}) },
	};
}

function merge_kanban_settings(parent, patch) {
	const out = { ...parent, ...patch };

	compose_item_list(out, parent, patch, "card_context_menu");
	compose_item_list(out, parent, patch, "bulk_actions");

	if (patch.callbacks || parent.callbacks) {
		out.callbacks = { ...(parent.callbacks || {}), ...(patch.callbacks || {}) };
	}
	if (patch.select_styles || parent.select_styles) {
		out.select_styles = { ...(parent.select_styles || {}), ...(patch.select_styles || {}) };
	}
	if (patch.options || parent.options) {
		out.options = { ...(parent.options || {}), ...(patch.options || {}) };
	}

	if (patch.boards || parent.boards) {
		out.boards = { ...(parent.boards || {}) };
		for (const [name, board_patch] of Object.entries(patch.boards || {})) {
			out.boards[name] = merge_kanban_settings(out.boards[name] || {}, board_patch);
		}
	}

	return out;
}

function compose_item_list(out, parent, patch, key) {
	if (patch[key] && parent[key]) {
		const base = parent[key];
		const child = patch[key];
		out[key] = (...args) => [...(base(...args) || []), ...(child(...args) || [])];
	}
}
